import React, { memo, ReactElement, ReactNode, useCallback, useContext, useRef, useSyncExternalStore } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Control, FieldError } from 'react-hook-form';
import FormGroup, { IFormGroup } from '../../atoms/FormGroup';
import { ReadonlyLock } from '../../atoms/ReadonlyIndicator';
import { ReadonlyIndicatorContext } from '../../context/ReadonlyIndicatorContext';
import clsx from 'clsx';
import { readFieldError, subscribeFieldErrors } from '../../lib/rhfBridge';
import './FormControl.scss';

export interface IFormErrorControlProps extends Omit<IFormGroup, 'invalid' | 'errorMessage'> {
    name: string;
    children: ReactNode;
    /**
     * Форма поля; без него — форма ближайшего `FormProvider`. `null` — подпись у элемента не из формы (внутри чужой
     * формы): ошибку не читает, не подписывается.
     */
    control?: any;
    /** Семантическая недоступность изменения, а не readonly атрибут триггера пикера. */
    readonlyAppearance?: 'field' | 'input' | 'password' | 'datetime' | 'hint';
}

type IFormControlViewProps = Omit<IFormErrorControlProps, 'control' | 'name'> & {
    error: FieldError | undefined;
};

const FormControlView = ({
    error,
    children,
    readonlyAppearance: requestedAppearance,
    ...props
}: IFormControlViewProps) => {
    const readonlyAppearance = useContext(ReadonlyIndicatorContext) ? requestedAppearance : undefined;
    const invalid = !!error;
    // invalid и правый слот — пропсы контролов; DOM-элементу (обёртке-div) они ушли бы атрибутами.
    const child =
        React.isValidElement(children) && typeof children.type !== 'string' ? (children as ReactElement<any>) : null;
    // Mantine input slot follows descriptions/counters and the control's height.
    // Preserve a caller-provided end adornment next to the lock.
    const inputLock = readonlyAppearance === 'input';
    const originalEnd = child?.props.rightSection;
    const endWidth = child?.props.rightSectionWidth ?? 'var(--input-height)';
    const readonlyInputProps = inputLock
        ? {
              rightSection: (
                  <span className="rf-readonly-input-section">
                      {originalEnd}
                      <ReadonlyLock />
                  </span>
              ),
              rightSectionWidth: originalEnd
                  ? `calc(${typeof endWidth === 'number' ? `${endWidth}px` : endWidth} + 24px)`
                  : 34,
              rightSectionPointerEvents: 'auto',
          }
        : {};

    return (
        <FormGroup
            invalid={invalid}
            // Всегда элемент, даже без текста: у FormControl абзац под полем есть всегда (см. FormGroup).
            errorMessage={<>{error?.message}</>}
            {...props}
            readOnlyHint={readonlyAppearance === 'hint' || props.readOnlyHint}
            controlClassName={clsx(
                props.controlClassName,
                readonlyAppearance &&
                    readonlyAppearance !== 'hint' && ['rf-readonly-field', `rf-readonly-field--${readonlyAppearance}`],
            )}
            controlAdornment={
                readonlyAppearance && readonlyAppearance !== 'hint' && !inputLock ? (
                    <ReadonlyLock />
                ) : (
                    props.controlAdornment
                )
            }
        >
            {child ? React.cloneElement(child, { invalid, ...readonlyInputProps }) : children}
        </FormGroup>
    );
};

const sameError = (a: FieldError | undefined, b: FieldError | undefined) =>
    a === b || (!!a && !!b && a.type === b.type && a.message === b.message);

/**
 * Ошибка поля подпиской на форму. События формы без имени (reset, trigger(), handleSubmit, clearErrors()) доходят до
 * всех подписчиков — снимок держит прежний объект, пока тип и текст ошибки те же, и React такой рендер пропускает.
 * Подписка не `exact`: поле-группа (`schedule`) получает и события вложенных полей (`schedule.hours`); лишние события
 * соседей по префиксу отсекает тот же снимок.
 */
const useFieldError = (control: Control<any>, name: string): FieldError | undefined => {
    const lastRef = useRef<FieldError | undefined>(undefined);
    const subscribe = useCallback(
        (onStoreChange: () => void) => subscribeFieldErrors(control, name, onStoreChange),
        [control, name],
    );
    const getSnapshot = () => {
        const error = readFieldError(control, name);
        if (!sameError(lastRef.current, error)) lastRef.current = error;
        return lastRef.current;
    };
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

type ISubscribedProps = IFormErrorControlProps & { control: Control<any> };

const SubscribedFormControl = ({ control, name, ...props }: ISubscribedProps) => (
    <FormControlView {...props} error={useFieldError(control, name)} />
);

// memo — только здесь: ContextFormControl перерисовывается на каждую смену значения FormProvider (рендер корня формы),
// а пропсы поля за memo-границей те же. С `control` поле рисует Controller, children всегда новые — memo не сработал бы.
const MemoSubscribedFormControl = memo(SubscribedFormControl);

/** Без `control`: подписка на форму провайдера, вне провайдера — без ошибки. */
const ContextFormControl = ({ name, ...props }: Omit<IFormErrorControlProps, 'control'>) => {
    const context = useFormContext();
    return context ? (
        <MemoSubscribedFormControl {...props} name={name} control={context.control} />
    ) : (
        <FormControlView {...props} error={undefined} />
    );
};

/**
 * Поле формы: подпись, ошибка, признак «только чтение». Ошибку поля читает своей подпиской по `name` — форма-хозяин
 * на неё не подписывается и не перерисовывается. `fieldState.error` из Controller не берём намеренно: его чтение
 * подписывает на ошибки сам Controller, и поле перерисовывается на каждое событие формы без имени (trigger(),
 * handleSubmit, clearErrors()), а снимок подписки здесь пропускает рендер, пока ошибка поля та же.
 */
const FormControl = ({ control, name, ...props }: IFormErrorControlProps) => {
    if (control === null) return <FormControlView {...props} error={undefined} />;
    return control ? (
        <SubscribedFormControl {...props} name={name} control={control} />
    ) : (
        <ContextFormControl {...props} name={name} />
    );
};

export default FormControl;
